import { Home, Smartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import { useLabAppBookings } from '@/features/lab-packages/api';
import type {
  LabAppBooking,
  LabAppBookingsQuery,
  LabBookingMode,
  LabBookingStatus,
} from '@/features/lab-packages/types';
import { LAB_STATUS_LABEL, type LabOrderStatus } from '@/features/orders/types';
import { klDate, klTime, whenLabel } from '@/pages/calendar/time';
import styles from '@/pages/queue/queue.module.css';
import { CancelAppBookingDialog } from './CancelAppBookingDialog';
import own from './AppBookings.module.css';

const SCOPE_OPTIONS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'all', label: 'Latest 200, with cancelled' },
];
const MODE_OPTIONS = [
  { value: 'clinic', label: 'At the branch' },
  { value: 'home', label: 'Home visit' },
];
const STATUS_OPTIONS = [
  { value: 'booked', label: 'Booked' },
  { value: 'cancelled', label: 'Cancelled' },
];

const orderStatus = (s: string) =>
  LAB_STATUS_LABEL[s as LabOrderStatus] ?? s.replace(/_/g, ' ');

/**
 * Lab desk "App bookings" (RL-1138, API RL-988): blood tests patients booked
 * in the app at this branch, at the branch or at home (window and address),
 * with the lab order. The steps the patient's tracker shows come from the
 * lab order (Open order: sample collected, dispatched, results). Cancel asks
 * for a reason; the patient is notified without it.
 */
export function AppBookingsCard({
  onOpenOrder,
}: {
  onOpenOrder: (labOrderId: string) => void;
}) {
  const { hasPermission } = useSession();
  const can = hasPermission('lab_orders:process');
  const [query, setQuery] = useState<LabAppBookingsQuery>({
    scope: 'upcoming',
    mode: '',
  });
  const [date, setDate] = useState('');
  const [status, setStatus] = useState<LabBookingStatus | ''>('');
  const [cancelling, setCancelling] = useState<LabAppBooking | null>(null);
  const q = useLabAppBookings(query);

  // The API filters by scope and where; date and status are filtered here.
  const rows = useMemo(
    () =>
      (q.data ?? []).filter(
        (b) =>
          (!date || klDate(b.startsAt) === date) &&
          (!status || b.status === status),
      ),
    [q.data, date, status],
  );
  const filtered = !!date || !!status || !!query.mode;

  const columns: Column<LabAppBooking>[] = [
    {
      key: 'when',
      header: 'When',
      render: (b) => (
        <>
          <p className={styles.name}>{whenLabel(b.startsAt)}</p>
          {b.mode === 'home' && (
            <p className={styles.sub}>Window until {klTime(b.endsAt)}</p>
          )}
        </>
      ),
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (b) => (
        <>
          <p className={styles.name}>{b.patient.fullName}</p>
          {b.patient.phone && <p className={styles.sub}>{b.patient.phone}</p>}
        </>
      ),
    },
    {
      key: 'what',
      header: 'Tests',
      render: (b) => (
        <>
          <p>{b.package?.name ?? 'Tests ordered by a doctor'}</p>
          <p className={styles.sub}>
            {b.kind === 'doctor_order' && b.labOrder.orderedBy
              ? `${b.labOrder.orderedBy.fullName} · `
              : ''}
            {b.fastingHours ? `Fast ${b.fastingHours} h` : 'No fasting'}
            {b.payer ? ` · Payer: ${b.payer.name}` : ''}
          </p>
        </>
      ),
    },
    {
      key: 'where',
      header: 'Where',
      render: (b) =>
        b.mode === 'home' ? (
          <>
            <Badge tone="info" icon={<Home size={12} aria-hidden="true" />}>
              Home visit
            </Badge>
            <p className={own.address}>{b.homeAddress ?? 'No address'}</p>
          </>
        ) : (
          <Badge>At the branch</Badge>
        ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (b) => (
        <>
          <p>{b.priceSen === null ? '—' : formatRM(b.priceSen)}</p>
          {b.homeFeeSen !== null && (
            <p className={styles.sub}>+ {formatRM(b.homeFeeSen)} home visit</p>
          )}
        </>
      ),
    },
    {
      key: 'order',
      header: 'Lab order',
      render: (b) => (
        <>
          <p className="mono">{b.labOrder.number ?? '—'}</p>
          <p className={styles.sub}>{orderStatus(b.labOrder.status)}</p>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (b) =>
        b.status === 'cancelled' ? (
          <>
            <Badge tone="danger" dot>
              Cancelled
            </Badge>
            {b.cancelReason && (
              <p className={styles.sub}>Reason: {b.cancelReason}</p>
            )}
          </>
        ) : (
          <Badge tone="success" dot>
            Booked
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (b) => (
        <div className={styles.badges}>
          <Button
            size="sm"
            onClick={() => onOpenOrder(b.labOrder.id)}
            aria-label={`Open the lab order for ${b.patient.fullName}`}
          >
            Open order
          </Button>
          {can && b.status === 'booked' && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setCancelling(b)}
              aria-label={`Cancel the booking for ${b.patient.fullName}`}
            >
              Cancel
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card
      title="App bookings"
      flush
      actions={
        <div className={own.filters}>
          <Field label="Show bookings">
            <Select
              value={query.scope}
              options={SCOPE_OPTIONS}
              onChange={(e) =>
                setQuery((v) => ({
                  ...v,
                  scope: e.target.value as LabAppBookingsQuery['scope'],
                }))
              }
            />
          </Field>
          <Field label="Where">
            <Select
              value={query.mode}
              placeholder="Branch and home"
              options={MODE_OPTIONS}
              onChange={(e) =>
                setQuery((v) => ({
                  ...v,
                  mode: e.target.value as LabBookingMode | '',
                }))
              }
            />
          </Field>
          <Field label="Date">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Status">
            <Select
              value={status}
              placeholder="Any status"
              options={STATUS_OPTIONS}
              onChange={(e) =>
                setStatus(e.target.value as LabBookingStatus | '')
              }
            />
          </Field>
        </div>
      }
    >
      <DataTable
        caption="Blood tests booked in the app"
        columns={columns}
        rows={rows}
        rowKey={(b) => b.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<Smartphone size={22} />}
            title={
              filtered
                ? 'No app bookings match these filters'
                : query.scope === 'upcoming'
                  ? 'No upcoming app bookings'
                  : 'No app bookings yet'
            }
            body={
              filtered
                ? undefined
                : 'Blood tests patients book in the app, at the branch or at home, appear here.'
            }
          />
        }
      />
      {cancelling && (
        <CancelAppBookingDialog
          booking={q.data?.find((b) => b.id === cancelling.id) ?? cancelling}
          onClose={() => setCancelling(null)}
        />
      )}
    </Card>
  );
}
